import { Component, inject, Input } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { NzBadgeModule } from "ng-zorro-antd/badge";
import { NzIconModule } from "ng-zorro-antd/icon";
import { Store } from "@ngrx/store";
import { selectorUnreadCount } from "@/app/stores/notifications/notifications.selectors";
import { StoreNotificationsModule } from "@/app/stores/notifications/store-notifications.module";
import { selectorConversations } from "@/app/stores/messages/messages.selectors";
import { StoreMessagesModule } from "@/app/stores/messages/store-messages.module";
import { CountUnreadMessagesPipe } from "@/app/core/pipes/filter-conversations-with-pending-requests.pipe";
import { selectorBusinessId, selectorCurrentUser } from "@/app/stores/auth/auth.selectors";
import { Business, KYCStatus } from "@/app/core/entities/business.entity";
import { User } from "@/app/core/entities/user.entity";
import { NzMessageModule, NzMessageService } from "ng-zorro-antd/message";
import { toSignal } from "@angular/core/rxjs-interop";

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

@Component({
  selector: "app-mobile-navbar",
  imports: [RouterModule, CommonModule, NzBadgeModule, NzIconModule, StoreNotificationsModule, StoreMessagesModule, CountUnreadMessagesPipe, NzMessageModule],
  templateUrl: "./mobile-navbar.component.html",
})
export class MobileNavbarComponent {
  @Input() navItems: any[] = [];
  @Input() currentRole: string = "";
  @Input() needsBeneficiarySetup: boolean | null = false;

  store = inject(Store);
  router = inject(Router);
  message = inject(NzMessageService);
  unreadCount$ = this.store.select(selectorUnreadCount);
  conversations$ = this.store.select(selectorConversations);
  user$ = this.store.select(selectorCurrentUser);
  readonly storeBusinessId = toSignal(this.store.select(selectorBusinessId), { initialValue: null as string | null });
  readonly KYCStatus = KYCStatus;

  businessForNav(user: User | null | undefined): Business | undefined {
    const businessId = this.resolveBusinessId() ?? this.storeBusinessId();
    if (businessId && user?.businessUsers?.length) {
      const match = user.businessUsers.find((bu) => bu.businessId === businessId || bu.business?.id === businessId);
      if (match?.business) {
        return match.business;
      }
    }
    return user?.businessUsers?.[0]?.business;
  }

  onNavItemClick(item: any) {
    const isRestricted = this.needsBeneficiarySetup && item.name !== "Home" && item.name !== "Wallets";
    if (isRestricted) {
      this.message.warning("Please set up your beneficiary bank account first to unlock all features.", { nzDuration: 3500 });
      return;
    }
    if (this.needsBeneficiarySetup && item.name === "Wallets") {
      this.router.navigate([item.route], { queryParams: { tab: "beneficiary" } });
      return;
    }
    this.router.navigate([item.route]);
  }

  private resolveBusinessId(): string | null {
    const firstPathSegment = this.router.url.split("/").filter(Boolean)[0];
    if (firstPathSegment && UUID_V4ISH_REGEX.test(firstPathSegment)) {
      return firstPathSegment;
    }
    return null;
  }
}
